import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";

import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";
import type { SwiperClass } from "swiper/react";
import { Swiper, SwiperSlide } from "swiper/react";

import { LongArrowBackwardIcon } from "@jti/brand-icons/long-arrow-backward";
import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";

import "swiper/css";

import { FlavourCarouselThumbnail } from "../FlavourCarouselThumbnail/FlavourCarouselThumbnail";

import styles from "./FlavourCarouselThumbnailsList.module.scss";

export type FlavourCarouselThumbnailsListProps = {
  thumbnails: {
    imageUrl: string;
    title: string;
    color: string;
    theme: "light" | "dark";
    index: number;
  }[];
  activeIndex: number;
  selectFlavour: (index: number) => void;
};

const CAROUSEL_PROGRESS_END = 1;
const CAROUSEL_PROGRESS_START = 0;

export const FlavourCarouselThumbnailsList: FC<FlavourCarouselThumbnailsListProps> = ({
  thumbnails,
  activeIndex,
  selectFlavour,
}) => {
  const { breakpoint } = useDeviceBreakpoint();
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const [progress, setProgress] = useState(0);
  const [canSwipeThumbnails, setCanSwipeThumbnails] = useState(false);
  const { __ } = useTranslations();

  const moveSlide = (direction: "next" | "prev") => {
    if (!swiper) return;
    const actions = {
      prev: () => swiper.slidePrev(),
      next: () => swiper.slideNext(),
    };
    actions[direction]();
  };

  const updateThumbnailsNavigationVisibility = useCallback(() => {
    if (swiper) setCanSwipeThumbnails(swiper.allowSlidePrev || swiper.allowSlideNext);
  }, [swiper]);

  useEffect(() => {
    if (swiper) {
      updateThumbnailsNavigationVisibility();
      swiper.slideTo(activeIndex);
    }
  }, [swiper, activeIndex, updateThumbnailsNavigationVisibility]);

  return (
    <div className={styles.thumbnailCarouselContainer}>
      <div className={styles.thumbnailCarouselWrapper}>
        <Swiper
          onSwiper={setSwiper}
          className={styles.thumbnailCarousel}
          onProgress={(_, progress) => {
            setProgress(progress);
          }}
          onResize={() => {
            updateThumbnailsNavigationVisibility();
          }}
          centeredSlides={breakpoint !== "desktop"}
          slidesPerView={"auto"}
        >
          {thumbnails.map(({ index, imageUrl, title, color, theme }) => (
            <SwiperSlide key={index} className={styles.thumbnailSlide}>
              <FlavourCarouselThumbnail
                selectFlavour={selectFlavour}
                imageUrl={imageUrl}
                title={title}
                color={color}
                index={index}
                isActive={activeIndex === index}
                theme={theme}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      <div
        className={classNames(styles.arrows, {
          [styles.arrowsHidden]: !canSwipeThumbnails,
        })}
      >
        <button
          disabled={progress === CAROUSEL_PROGRESS_START}
          onClick={() => moveSlide("prev")}
          className={styles.arrowButton}
          data-testid="thumbnailCarouselPrevButton"
          aria-label={__("carousel.previous")}
        >
          <LongArrowBackwardIcon aria-hidden="true" />
        </button>
        <button
          disabled={progress === CAROUSEL_PROGRESS_END}
          onClick={() => moveSlide("next")}
          className={styles.arrowButton}
          data-testid="thumbnailCarouselNextButton"
          aria-label={__("carousel.next")}
        >
          <LongArrowForwardIcon aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};
